import React, { useState, useEffect, useRef } from 'react';
import { oktaService } from '../../services/oktaService.js';
import { adminService } from '../../services/adminService.js';

// Pure helper functions moved outside component to avoid dependency issues
const toLowerSafe = (value) => (value ? String(value).toLowerCase() : '');

const isEmbarcRoleName = (roleNameLower) => roleNameLower === 'embarc_user';

export default function OktaUserRegistration({ editingUser, onCreated, onCancel }) {
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');  
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  /** Free-text + suggestions menu; existing org names or a new name (trimmed on submit). */
  const [organizationInput, setOrganizationInput] = useState('');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgHighlightIndex, setOrgHighlightIndex] = useState(-1);
  const orgComboboxRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validationError, setValidationError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Constants for duplicated style literals
  const CURSOR_POINTER = 'pointer';
  const CURSOR_NOT_ALLOWED = 'not-allowed';
  const COLOR_WHITE = 'white';
  const COLOR_DISABLED_BG = '#f0f0f0';

  // Common styles
  const getInputStyles = (isDisabled) => ({
    backgroundColor: isDisabled ? COLOR_DISABLED_BG : COLOR_WHITE,
    cursor: isDisabled ? CURSOR_NOT_ALLOWED : CURSOR_POINTER
  });

  const getHelpTextStyle = () => ({
    color: '#666',
    fontSize: '0.8em'
  });

  const getEditableHelpTextStyle = () => ({
    color: '#007acc',
    fontSize: '0.8em'
  });

  // Reusable components to reduce code duplication
  const HelpText = ({ children, isEditable = false }) => (
    <small style={isEditable ? getEditableHelpTextStyle() : getHelpTextStyle()}>
      {children}
    </small>
  );

  // Fetch master data on component mount
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        setLoading(true);
        const [orgsResponse, rolesResponse] = await Promise.all([
          adminService.getAllOrganizations(),
          adminService.getAllUserRoles()
        ]);

        setOrganizations(orgsResponse || []);
        setUserRoles(rolesResponse || []);

        // Don't set default values - let user select initially
        // Both Role and Organization will show "Select Role" and "Select Organization" placeholders
      } catch (error) {
        console.error('Error fetching master data:', error);
        setValidationError('Failed to load organizations and roles. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();
  }, []);

  // When email is cleared, reset role/org — must NOT depend on organizationInput or typing org while email is empty will be wiped every keystroke.
  useEffect(() => {
    if (editingUser) {
      return;
    }
    if (email === '') {
      setRoleId('');
      setOrganizationInput('');
      setOrgDropdownOpen(false);
      setOrgHighlightIndex(-1);
      setValidationError('');
    }
  }, [email, editingUser]);

  useEffect(() => {
    if (!orgDropdownOpen) {
      return undefined;
    }
    const onDocMouseDown = (e) => {
      if (orgComboboxRef.current && !orgComboboxRef.current.contains(e.target)) {
        setOrgDropdownOpen(false);
        setOrgHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [orgDropdownOpen]);

  // Populate form when editing user
  useEffect(() => {
    if (editingUser && organizations.length > 0 && userRoles.length > 0) {
      setUsername(editingUser.username || '');
      setFirstName(editingUser.firstName || '');
      setLastName(editingUser.lastName || '');
      setEmail(editingUser.email || '');
      setOrganizationInput((editingUser.organization || '').trim());
      // Find matching role
      const matchingRole = userRoles.find(role =>
        role.role?.toLowerCase() === editingUser.role?.toLowerCase()
      );
      if (matchingRole) {
        setRoleId(matchingRole.id.toString());
      }
      setValidationError('');
    }
  }, [editingUser, organizations, userRoles]);

  const orgSearchQuery = organizationInput.trim().toLowerCase();
  const filteredOrganizations = [...organizations]
    .filter((o) => !orgSearchQuery || (o.name && o.name.toLowerCase().includes(orgSearchQuery)))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

  const selectOrganizationName = (name) => {
    setOrganizationInput(name);
    setOrgDropdownOpen(false);
    setOrgHighlightIndex(-1);
    setValidationError('');
  };

  const handleOrganizationInputChange = (e) => {
    setOrganizationInput(e.target.value);
    setOrgDropdownOpen(true);
    setOrgHighlightIndex(-1);
    if (validationError) {
      setValidationError('');
    }
  };

  const handleOrgInputFocus = () => {
    setOrgDropdownOpen(true);
  };

  const handleOrgInputKeyDown = (e) => {
    const list = filteredOrganizations;
    if (e.key === 'Escape') {
      if (orgDropdownOpen) {
        e.preventDefault();
        setOrgDropdownOpen(false);
        setOrgHighlightIndex(-1);
      }
      return;
    }

    if (list.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOrgDropdownOpen(true);
      setOrgHighlightIndex((prev) => {
        if (prev < 0) return 0;
        return Math.min(prev + 1, list.length - 1);
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOrgDropdownOpen(true);
      setOrgHighlightIndex((prev) => Math.max(prev <= 0 ? 0 : prev - 1, 0));
      return;
    }

    if (e.key === 'Enter' && orgDropdownOpen && orgHighlightIndex >= 0) {
      const org = list[orgHighlightIndex];
      if (org?.name != null) {
        e.preventDefault();
        selectOrganizationName(org.name);
      }
    }
  };

  const handleOrgOptionMouseDown = (e, name) => {
    e.preventDefault();
    selectOrganizationName(name);
  };

  // Handle role change (no validation here - only on Create button click)
  const handleRoleChange = (e) => {
    const newRoleId = e.target.value;
    setRoleId(newRoleId);
    if (validationError) {
      setValidationError('');
    }
  };

  const getSelectedRoleAndOrg = () => {
    const selectedRole = userRoles.find(r => r.id.toString() === roleId);
    if (!selectedRole) {
      setValidationError('Please select a valid role');
      setSaving(false);
      return null;
    }

    const trimmedOrg = organizationInput.trim();
    if (!trimmedOrg) {
      setValidationError('Please enter or select an organization');
      setSaving(false);
      return null;
    }
    if (trimmedOrg.length > 50) {
      setValidationError('Organization name must be at most 50 characters');
      setSaving(false);
      return null;
    }

    const existingOrg = organizations.find(o => o.name?.toLowerCase() === trimmedOrg.toLowerCase());
    const selectedOrg = existingOrg || { id: null, name: trimmedOrg };
    return { selectedRole, selectedOrg };
  };

  // Helper function to handle user update
  const handleUserUpdate = async (selectedRole, selectedOrg) => {
    const effectiveUsername = (username || '').trim() || email;
    const displayName = effectiveUsername;
    const updatePayload = {
      userId: editingUser.userId,
      oktaUserId: editingUser.oktaUserId,
      username: effectiveUsername,
      firstName,
      lastName,
      email,
      role: selectedRole.role,
      organization: selectedOrg.name,
      organizationId: selectedOrg.id,
      roleId: selectedRole.id,
      isExternal: !isEmbarcRoleName(toLowerSafe(selectedRole.role))
    };

    const result = await oktaService.updateLocalUser(updatePayload);
    
    if (result.success) {
      const successMessage = `User '${displayName}' updated successfully in local database!`;
      setRegistrationResult({
        type: 'success',
        message: result.message || successMessage
      });
      alert(successMessage);
      if (onCreated) {
        onCreated(result);
      }
    } else {
      const errorMessage = result.message || `Failed to update user '${displayName}' in local database`;
      setRegistrationResult({
        type: 'error',
        message: errorMessage
      });
      alert(errorMessage);
    }
  };

  // Helper function to build success message for Okta registration
  const buildOktaSuccessMessage = (result) => {
    const displayName = (username || '').trim() || email;
    let successMessage = `User '${displayName}' created successfully in Okta!`;
    if (result.requiresActivation && result.activationUrl) {
      successMessage += `\n\nActivation required. Activation URL: ${result.activationUrl}`;
    }
    return successMessage;
  };

  // Helper function to handle user creation in Okta
  const handleUserCreation = async (selectedRole, selectedOrg) => {
    const roleName = selectedRole.role?.toLowerCase() || '';
    const isExternalId = roleName !== 'embarc_user';

    // Use email as username when username is empty or whitespace-only
    const effectiveUsername = (username || '').trim() || email;

    const payload = {
      username: effectiveUsername,
      firstName,
      lastName,
      email,
      role: selectedRole.role,
      organization: selectedOrg.name,
      isExternalId: isExternalId
    };

    const result = await oktaService.registerUser(payload);
    
    if (result.success) {
      setRegistrationResult({
        type: 'success',
        message: result.message,
        oktaUserId: result.oktaUserId,
        activationUrl: result.activationUrl,
        requiresActivation: result.requiresActivation
      });
      const successMessage = buildOktaSuccessMessage(result);
      alert(successMessage);
      if (onCreated) {
        onCreated(result);
      }
    } else {
      setRegistrationResult({
        type: 'error',
        message: result.message || 'Failed to create user in Okta'
      });
      alert(result.message || 'Failed to create user in Okta');
    }
  };

  // Helper function to handle errors
  const handleSubmitError = (err) => {
    console.error(`Error ${editingUser ? 'updating' : 'registering'} user:`, err);
    const errorMessage = err?.response?.data?.message || `Failed to ${editingUser ? 'update user' : 'create user in Okta'}`;
    setRegistrationResult({
      type: 'error',
      message: errorMessage
    });
    alert(errorMessage);
  };

  // Helper function to get submit button text
  const getSubmitButtonText = () => {
    if (saving) {
      return editingUser ? 'Updating...' : 'Creating in Okta...';
    }
    return editingUser ? 'Update User' : 'Create in Okta';
  };

  // Helper function for button styles to reduce duplication
  const getButtonStyle = (backgroundColor, disabled = false) => ({
    backgroundColor: disabled ? '#ccc' : backgroundColor,
    padding: '6px 12px',
    border: 'none',
    cursor: disabled ? CURSOR_NOT_ALLOWED : CURSOR_POINTER
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submit
    const usernameValidationMsg = validateUsername(username);
    const firstNameValidationMsg = validateName(firstName);
    const lastNameValidationMsg = validateName(lastName);
    const emailValidationMsg = validateEmail(email);
    setUsernameError(usernameValidationMsg);
    setFirstNameError(firstNameValidationMsg);
    setLastNameError(lastNameValidationMsg);
    setEmailError(emailValidationMsg);

    if (usernameValidationMsg || firstNameValidationMsg || lastNameValidationMsg || emailValidationMsg) {
      setSaving(false);
      return;
    }

    setSaving(true);
    setRegistrationResult(null);
    try {
      const roleAndOrg = getSelectedRoleAndOrg();
      if (!roleAndOrg) {
        return;
      }

      const { selectedRole, selectedOrg } = roleAndOrg;

      if (editingUser) {
        await handleUserUpdate(selectedRole, selectedOrg);
      } else {
        await handleUserCreation(selectedRole, selectedOrg);
      }
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setSaving(false);
    }
  };

  const clearForm = () => {
    // In edit mode, only clear editable fields (Role and Organization)
    if (editingUser) {
      setRoleId('');
      setOrganizationInput('');
      setOrgDropdownOpen(false);
      setOrgHighlightIndex(-1);
      setValidationError('');
      setRegistrationResult(null);
    } else {
      setUsername('');
      setFirstName('');
      setLastName('');
      setEmail('');
      setRoleId('');
      setOrganizationInput('');
      setOrgDropdownOpen(false);
      setOrgHighlightIndex(-1);
      setValidationError('');
      setRegistrationResult(null);
    }
  };

  if (loading) {
    return (
      <div style={{ border: '1px solid #e0e0e0', padding: '12px', borderRadius: '6px', marginTop: '12px', marginBottom: '12px', background: '#fafafa' }}>
        <h4>Create User</h4>
        <p>Loading organizations and roles...</p>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #e0e0e0', padding: '12px', borderRadius: '6px', marginTop: '12px', marginBottom: '12px', background: '#fafafa' }}>
      <h4 style={{ color: '#007acc', marginBottom: '16px' }}>
        {editingUser ? 'Edit User Details' : 'Create User in Okta'}
      </h4>
      
      {registrationResult && (
        <div style={{ 
          padding: '12px', 
          marginBottom: '16px', 
          borderRadius: '4px',
          backgroundColor: registrationResult.type === 'success' ? '#d4edda' : '#f8d7da',
          border: `1px solid ${registrationResult.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          color: registrationResult.type === 'success' ? '#155724' : '#721c24'
        }}>
          <strong>{registrationResult.type === 'success' ? 'Success:' : 'Error:'}</strong> {registrationResult.message}
          {registrationResult.requiresActivation && (
            <div style={{ marginTop: '8px', fontSize: '0.9em', fontStyle: 'italic' }}>
              User requires activation. Check activation URL in the response.
            </div>
          )}
        </div>
      )}
      
      <form className="okta-registration-form" onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label>Username</label>
            <input 
              className="form-control" 
              value={username}
              maxLength={100}
              onChange={e => {
                setUsername(e.target.value);
                const err = validateUsername(e.target.value);
                setUsernameError(err);
              }}
              onBlur={e => {
                const err = validateUsername(e.target.value);
                setUsernameError(err);
              }}
              title={editingUser ? undefined : 'Leave empty to use email as username'}
              disabled={!!editingUser}
              style={getInputStyles(!!editingUser)}
            />
            {usernameError && (
              <div style={{ color: '#d32f2f', fontSize: '13px', marginTop: '2px' }}>{usernameError}</div>
            )}
            {!editingUser && (
              <HelpText>If empty, email will be used as username</HelpText>
            )}
            {editingUser && (
              <HelpText>Username cannot be changed when editing</HelpText>
            )}
          </div>

          <div style={{ flex: '1', minWidth: '200px' }}>
            <label>First Name <span style={{ color: 'red' }}>*</span></label>
            <input 
              className="form-control"
              value={firstName}
              maxLength={50}
              onChange={e => {
                setFirstName(e.target.value);
                const err = validateName(e.target.value);
                setFirstNameError(err);
              }}
              onBlur={e => {
                const err = validateName(e.target.value);
                setFirstNameError(err);
              }}
              required={!editingUser}
              disabled={!!editingUser}
              style={getInputStyles(!!editingUser)}
            />
            {firstNameError && (
              <div style={{ color: '#d32f2f', fontSize: '13px', marginTop: '2px' }}>{firstNameError}</div>
            )}
            {editingUser && (
              <HelpText>First Name cannot be changed when editing</HelpText>
            )}
          </div>

          <div style={{ flex: '1', minWidth: '200px' }}>
            <label>Last Name <span style={{ color: 'red' }}>*</span></label>
            <input 
              className="form-control"
              value={lastName}
              maxLength={50}
              onChange={e => {
                setLastName(e.target.value);
                const err = validateName(e.target.value);
                setLastNameError(err);
              }}
              onBlur={e => {
                const err = validateName(e.target.value);
                setLastNameError(err);
              }}
              required={!editingUser}
              disabled={!!editingUser}
              style={getInputStyles(!!editingUser)}
            />
            {lastNameError && (
              <div style={{ color: '#d32f2f', fontSize: '13px', marginTop: '2px' }}>{lastNameError}</div>
            )}
            {editingUser && (
              <HelpText>Last Name cannot be changed when editing</HelpText>
            )}
          </div>

          <div style={{ flex: '1', minWidth: '220px' }}>
            <label>Email <span style={{ color: 'red' }}>*</span></label>
            <input 
              type="email" 
              className="form-control"
              value={email}
              maxLength={100}
              onChange={e => {
                setEmail(e.target.value);
                const err = validateEmail(e.target.value);
                setEmailError(err);
              }}
              onBlur={e => {
                const err = validateEmail(e.target.value);
                setEmailError(err);
              }}
              disabled={!!editingUser}
              required 
              style={getInputStyles(!!editingUser)}
            />
            {emailError && (
              <div style={{ color: '#d32f2f', fontSize: '13px', marginTop: '2px' }}>{emailError}</div>
            )}
            {editingUser && (
              <HelpText>Email cannot be changed when editing</HelpText>
            )}
          </div>

          <div style={{ flex: '1', minWidth: '160px' }}>
            <label>Role <span style={{ color: 'red' }}>*</span> {editingUser ? '(Editable)' : ''}</label>
            <select 
              className="form-control"
              value={roleId}
              onChange={handleRoleChange}
              required
              style={getInputStyles(false)}
            >
              <option value="">Select Role</option>
              {userRoles.map((role) => (
                <option key={role.id} value={role.id.toString()}>
                  {role.role}
                </option>
              ))}
            </select>
            {editingUser && (
              <HelpText isEditable>Role can be updated when editing</HelpText>
            )}
          </div>

          <div style={{ flex: '1', minWidth: '224px', maxWidth: '336px' }}>
            <label htmlFor="okta-org-input">
              Organization <span style={{ color: 'red' }}>*</span> {editingUser ? '(Editable)' : ''}
            </label>
            <div className="okta-org-combobox-wrap" ref={orgComboboxRef}>
              <input
                id="okta-org-input"
                type="text"
                className="form-control okta-org-combobox-input"
                role="combobox"
                aria-expanded={orgDropdownOpen}
                aria-controls="okta-org-listbox"
                aria-autocomplete="list"
                aria-activedescendant={
                  orgDropdownOpen && orgHighlightIndex >= 0
                    ? `okta-org-opt-${orgHighlightIndex}`
                    : undefined
                }
                value={organizationInput}
                onChange={handleOrganizationInputChange}
                onFocus={handleOrgInputFocus}
                onKeyDown={handleOrgInputKeyDown}
                required
                maxLength={50}
                placeholder="Select or type organization name"
                autoComplete="off"
                style={getInputStyles(false)}
              />
              {orgDropdownOpen && filteredOrganizations.length > 0 && (
                <ul id="okta-org-listbox" role="listbox" className="okta-org-combobox-list">
                  {filteredOrganizations.map((org, idx) => (
                    <li
                      key={org.id}
                      id={`okta-org-opt-${idx}`}
                      role="option"
                      aria-selected={idx === orgHighlightIndex}
                      className="okta-org-combobox-option"
                      onMouseDown={(ev) => handleOrgOptionMouseDown(ev, org.name)}
                      onMouseEnter={() => setOrgHighlightIndex(idx)}
                    >
                      {org.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <HelpText isEditable={!!editingUser}>
              Choose an existing organization or type a new name.
            </HelpText>
          </div>
        </div>

        {validationError && (
          <div style={{ marginTop: '8px', color: '#d32f2f', fontSize: '14px', fontWeight: '500' }}>
            {validationError}
          </div>
        )}

        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={saving || !!validationError || !!usernameError || !!firstNameError || !!lastNameError || !!emailError}
            style={getButtonStyle('#009688', saving || !!validationError || !!usernameError || !!firstNameError || !!lastNameError || !!emailError)}
          >
            {getSubmitButtonText()}
          </button>
          
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={clearForm} 
            disabled={saving} 
            style={getButtonStyle('#6c757d', saving)}
          >
            Clear Form
          </button>
          
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onCancel} 
            disabled={saving} 
            style={getButtonStyle('#009688', saving)}
          >
            Cancel
          </button>

        </div>
      </form>
    </div>
  );
}

const validateUsername = (value) => {
  if (!value) {
    return '';
  }
  if (!/^[A-Za-z0-9._]+$/.test(value)) {
    return 'Username can only contain letters, numbers, dot, or underscore.';
  }
  if (/^\.|\.$|^_|_$/.test(value)) {
    return 'Username cannot start or end with dot or underscore.';
  }
  if (/\.\.|__|\._|_\./.test(value)) {
    return 'Username cannot have consecutive dots/underscores or mix them.';
  }
  const dotCount = (value.match(/\./g) || []).length;
  const underscoreCount = (value.match(/_/g) || []).length;
  if (dotCount > 1 || underscoreCount > 1) {
    return 'Username can have at most one dot or one underscore.';
  }
  if (dotCount === 1 && underscoreCount === 1) {
    return 'Username can have only one dot or one underscore, not both.';
  }
  return '';
};
const validateName = (value) => {
  if (!value) {
    return 'This field is required.';
  }
  if (!/^[A-Za-z]+$/.test(value)) {
    return 'Only alphabets are allowed.';
  }
  return '';
};
const validateEmail = (value) => {
  if (!value) {
    return 'Email is required.';
  }
  // RFC 5322 compliant email validation with security best practices
  if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
    return 'Invalid email address.';
  }
  return '';
};